import React, { useRef, useState, useEffect } from 'react';
import { motion, AnimatePresence, useScroll, useTransform } from 'framer-motion';
import { Cpu, CheckCircle2 } from 'lucide-react';
import useIsMobile from '../hooks/useIsMobile';

// cx/cy = orbit positions (vw/vh from center) | ex/ey = entry positions (off-screen edges)
const TASK_CARDS = [
  { id: 1, text: 'Propose to Preeti before her arranged marriage', emoji: '💍', cx: -38, cy: -20, rot: -12, ex: -130, ey: -50 },
  { id: 2, text: 'Schedule mental breakdown between 2 and 2:15 PM', emoji: '😭', cx: 32, cy: -28, rot: 8, ex: 120, ey: -80 },
  { id: 3, text: "Attend some unknown Sharma Ji's wedding", emoji: '🍛', cx: -15, cy: -35, rot: -15, ex: -20, ey: -95 },
  { id: 4, text: 'Stare blankly at ceiling & contemplate mortality', emoji: '🪫', cx: 40, cy: 6, rot: 5, ex: 130, ey: 5 },
  { id: 5, text: "Donate ₹3000 to gym I haven't visited since 2023", emoji: '🏋️', cx: -32, cy: 15, rot: -5, ex: -130, ey: 60 },
  { id: 6, text: "Mom's birthday — this Sunday", emoji: '🎂', cx: 22, cy: -30, rot: 11, ex: 65, ey: -95 },
  { id: 7, text: 'Dentist appt — reschedule??', emoji: '🦷', cx: -42, cy: 8, rot: -8, ex: -130, ey: 20 },
  { id: 8, text: 'Reply to client email ASAP', emoji: '📧', cx: 18, cy: 20, rot: 6, ex: 125, ey: 45 },
  { id: 9, text: 'Review contract before signing', emoji: '📄', cx: -25, cy: -10, rot: -14, ex: -90, ey: -90 },
  { id: 10, text: 'Grocery: milk, eggs, coffee', emoji: '🛒', cx: 30, cy: -12, rot: 9, ex: 100, ey: -85 },
  { id: 11, text: 'That article someone sent me', emoji: '📖', cx: -10, cy: 30, rot: -4, ex: -20, ey: 95 },
];

const LOG_CARDS = [
  { id: 12, text: 'Did I call mom??', emoji: '📞', cx: 35, cy: 26, rot: 7, ex: 125, ey: 70 },
  { id: 13, text: 'What happened in that meeting?', emoji: '🤔', cx: -30, cy: 28, rot: -6, ex: -125, ey: 80 },
  { id: 14, text: 'That idea from this morning…', emoji: '💡', cx: 12, cy: 36, rot: 14, ex: 15, ey: 95 },
  { id: 15, text: 'Did I even work out today??', emoji: '❓', cx: -18, cy: 38, rot: -10, ex: -50, ey: 95 },
  { id: 16, text: 'What feedback did they give?', emoji: '🗣️', cx: 38, cy: -18, rot: 4, ex: 125, ey: -65 },
  { id: 17, text: '3 calls today? or was it 4?', emoji: '😵', cx: -42, cy: -5, rot: -3, ex: -130, ey: -15 },
  { id: 18, text: 'Had the best idea in the shower', emoji: '🚿', cx: 20, cy: -38, rot: 10, ex: 40, ey: -95 },
  { id: 19, text: 'Missed the 9am standup… again', emoji: '😬', cx: -35, cy: -25, rot: -11, ex: -130, ey: -60 },
  { id: 20, text: 'Great podcast — who was it?', emoji: '🎧', cx: 42, cy: 18, rot: 6, ex: 130, ey: 40 },
  { id: 21, text: 'I owe Raj money — how much?', emoji: '💸', cx: -20, cy: 10, rot: -7, ex: -130, ey: 30 },
];

const ORG_TASKS = [
  { label: 'CRISIS', text: 'Propose to Preeti (She will say no)', due: 'Before Sunday', color: '#EF4444' },
  { label: 'WELLNESS', text: 'Cry in the corporate bathroom cubicle', due: 'Daily · 2:00 PM', color: '#3B82F6' },
  { label: 'OBLIGATION', text: "Sharma Ji's Wedding (Target: Free Paneer)", due: 'Today · 8:30 PM', color: '#F97316' },
  { label: 'THE VOID', text: 'Stare into the abyss (It stares back)', due: 'Tonight · 2:00 AM', color: '#8B5CF6' },
  { label: 'CHARITY', text: "Subsidize fit people's gym equipment", due: 'Monthly auto-debit', color: '#EC4899' },
];

const ORG_LOGS = [
  { text: 'Noticed a cool food stall on 5th st.', time: 'Today, 1:15 PM' },
  { text: 'Team sync — design review done ✓', time: 'Today, 2:30 PM' },
  { text: 'Best shower idea — captured it', time: 'Today, 8:10 AM' },
  { text: 'Called mom to check in', time: 'Today, 11:15 AM' },
  { text: 'Morning workout · 45 min run', time: 'Today, 7:00 AM' },
];

// No spring on scroll — direct 1:1 tracking is fast and responsive

// ── Single card — fully scroll-driven ─────────────────────────────────────────
const ChaosCard = ({ card, isTask, isMobile, smoothProgress, staggerOffset }) => {
  const color = isTask ? '#8B5CF6' : '#F59E0B';
  const bg = isTask ? 'rgba(60,30,100,0.92)' : 'rgba(100,65,5,0.92)';
  const bdr = isTask ? 'rgba(139,92,246,0.9)' : 'rgba(245,158,11,0.9)';
  const glow = isTask ? 'rgba(139,92,246,0.55)' : 'rgba(245,158,11,0.55)';
  const textClr = isTask ? 'rgba(220,200,255,1)' : 'rgba(255,235,180,1)';

  // Scroll timeline per card (section 750vh, no spring — direct scroll)
  // Phase 1  0.00 → 0.18+s   fly in from off-screen edge
  // Phase 2  0.18+s → 0.55   hold at spread position — readable
  // Phase 3  0.55 → 0.66     drift inward toward mic & fade
  const t0 = staggerOffset;
  const t1 = 0.18 + staggerOffset;   // arrives
  const t2 = 0.55;                   // drift-in starts
  const t3 = 0.66;                   // fully inside mic

  const x = useTransform(smoothProgress, [t0, t1, t2, t3],
    [`${card.ex}vw`, `${card.cx}vw`, `${card.cx * 0.08}vw`, '0vw']);
  const y = useTransform(smoothProgress, [t0, t1, t2, t3],
    [`${card.ey}vh`, `${card.cy}vh`, `${card.cy * 0.08}vh`, '0vh']);
  const opacity = useTransform(smoothProgress,
    [t0, t0 + 0.025, t2, t3], [0, 1, 1, 0]);
  const scale = useTransform(smoothProgress,
    [t0, t1, t2, t3], [0.15, isMobile ? 0.6 : 1, isMobile ? 0.55 : 0.9, 0.0]);
  // Spin only on entry; settles flat and stays readable
  const rotate = useTransform(smoothProgress,
    [t0, t1], [card.rot * 5, card.rot]);

  return (
    <motion.div
      className="absolute z-20 pointer-events-none"
      style={{ left: '50%', top: '50%', marginLeft: -115, marginTop: -26, x, y, opacity, scale, rotate, willChange: 'transform' }}
    >
      <div style={{
        background: bg,
        border: `1.5px solid ${bdr}`,
        borderRadius: 12,
        padding: '10px 18px',
        whiteSpace: 'nowrap',
        backdropFilter: 'blur(12px)',
        boxShadow: `0 0 28px ${glow}, 0 4px 20px rgba(0,0,0,0.6), inset 0 1px 0 rgba(255,255,255,0.12)`,
        fontFamily: "'JetBrains Mono', monospace",
        fontSize: 12.5,
        color: textClr,
        fontWeight: 600,
      }}>
        {card.emoji} {card.text}
        <span style={{
          display: 'inline-block', marginLeft: 10, width: 5, height: 5,
          borderRadius: '50%', background: color, verticalAlign: 'middle',
          boxShadow: `0 0 6px ${color}`
        }} />
      </div>
    </motion.div>
  );
};

// ── Animated counter — counts up then freezes ──────────────────────────────────
const LiveCounter = () => {
  const [val, setVal] = useState(0);
  useEffect(() => {
    let n = 0;
    const id = setInterval(() => {
      n += Math.floor(Math.random() * 4) + 1;
      if (n >= 21) { setVal(21); clearInterval(id); }
      else setVal(n);
    }, 60);
    return () => clearInterval(id);
  }, []);
  return <span style={{ color: '#10B981', fontVariantNumeric: 'tabular-nums' }}>{val}</span>;
};

// ── Main component ─────────────────────────────────────────────────────────────
const ChaosToClarity = () => {
  const sectionRef = useRef(null);
  const [phase, setPhase] = useState('chaos');

  const { scrollYProgress } = useScroll({ target: sectionRef, offset: ['start start', 'end end'] });
  // Direct scroll — no spring lag. Performance-critical section.
  const smoothProgress = scrollYProgress;

  // Phase detection — 750vh section:
  //   chaos:      0.00 → 0.55  (~412vh)
  //   converging: 0.55 → 0.70  (~112vh ≈ 1 screen)
  //   organized:  0.70 → 1.00  (~225vh ≈ 2 screens)
  useEffect(() => {
    const unsub = scrollYProgress.on('change', (p) => {
      let next = 'chaos';
      if (p >= 0.55 && p < 0.70) next = 'converging';
      else if (p >= 0.70) next = 'organized';
      setPhase(prev => prev !== next ? next : prev);
    });
    return unsub;
  }, [scrollYProgress]);

  // ── Group-level transforms (no spring, direct scroll) ────────────────────────
  const groupOpacity = useTransform(smoothProgress, [0.52, 0.60], [1, 0]);

  const brainOpacity = useTransform(smoothProgress, [0.65, 0.75], [1, 0.1]); // Keeps it slightly visible as a watermark in Organized phase

  const textOpacity = useTransform(smoothProgress, [0, 0.05, 0.45, 0.55], [0, 1, 1, 0]);
  const textY = useTransform(smoothProgress, [0, 0.55], [20, -40]);
  const uiOpacity = useTransform(smoothProgress, [0, 0.05], [0, 1]);

  const isChaos = phase === 'chaos';
  const isConverging = phase === 'converging';
  const isOrganized = phase === 'organized';

  const isMobile = useIsMobile();

  // On mobile, aggressively reduce payload to 8 cards (4 tasks, 4 logs) to prevent overlapping chaos.
  const activeTaskCards = isMobile ? TASK_CARDS.slice(0, 4) : TASK_CARDS;
  const activeLogCards = isMobile ? LOG_CARDS.slice(0, 4) : LOG_CARDS;

  const allCards = [
    ...activeTaskCards.map((c, i) => ({
      ...c,
      isTask: true,
      staggerOffset: i * 0.004,
      cx: isMobile ? c.cx * 0.6 : c.cx,
      cy: isMobile ? c.cy * 0.7 : c.cy,
    })),
    ...activeLogCards.map((c, i) => ({
      ...c,
      isTask: false,
      staggerOffset: (activeTaskCards.length + i) * 0.004,
      cx: isMobile ? c.cx * 0.6 : c.cx,
      cy: isMobile ? c.cy * 0.7 : c.cy,
    })),
  ];

  return (
    <section ref={sectionRef} className="relative w-full" style={{ height: isMobile ? '4500px' : '750vh' }}>
      <div className="sticky top-0 w-full overflow-hidden flex items-center justify-center" style={{ height: '100vh' }}>

        {/* Top label */}
        <motion.p
          className="absolute top-12 md:top-10 left-1/2 -translate-x-1/2 z-40 text-[9px] md:text-xs font-mono uppercase tracking-[0.2em] md:tracking-[0.3em] text-[#444] w-full px-4 text-center"
          style={{ opacity: uiOpacity }}
        >
          {isOrganized ? 'One input. Everything organized.' : 'Your brain right now'}
        </motion.p>

        {/* ── Chaos headline ── */}
        <motion.div
          className="absolute top-20 left-1/2 text-center z-30 pointer-events-none w-full px-6"
          style={{ opacity: textOpacity, y: textY, x: '-50%' }}
        >
          <h2 className="text-3xl md:text-5xl font-medium text-[#EDEDED] tracking-tighter mb-4">
            You're holding all of this.<br />Right now.
          </h2>
          <div className="flex justify-center gap-8">
            <span className="text-xs font-mono flex items-center gap-2" style={{ color: '#8B5CF6' }}>
              <span className="w-2 h-2 rounded-full inline-block" style={{ background: '#8B5CF6', boxShadow: '0 0 8px #8B5CF6' }} />
              Tasks & deadlines
            </span>
            <span className="text-xs font-mono flex items-center gap-2" style={{ color: '#F59E0B' }}>
              <span className="w-2 h-2 rounded-full inline-block" style={{ background: '#F59E0B', boxShadow: '0 0 8px #F59E0B' }} />
              Memories fading
            </span>
          </div>
        </motion.div>

        {/* ── Central brain / mic ── */}
        <AnimatePresence>
          <motion.div
            key="mic"
            className="absolute z-50 top-1/2 left-1/2 flex items-center justify-center"
            initial={{ scale: 0, opacity: 0 }}
            animate={{ scale: 1, opacity: 1 }}
            exit={{ scale: 0, opacity: 0, transition: { duration: 0.4 } }}
            transition={{ duration: 0.5 }}
            style={{ opacity: brainOpacity, marginTop: -36, marginLeft: -36, width: 72, height: 72 }}
          >
            {/* Orbit rings */}
            {[80, 120, 165].map((r, i) => (
              <motion.div key={i} className="absolute rounded-full" style={{
                width: r, height: r,
                top: '50%', left: '50%', marginTop: -r / 2, marginLeft: -r / 2,
                border: `1px solid rgba(139,92,246,${0.18 - i * 0.05})`
              }}
                animate={{ scale: [1, 1.9, 1], opacity: [0.4, 0, 0.4] }}
                transition={{ duration: 2.4, delay: i * 0.5, repeat: Infinity }}
              />
            ))}

            {/* Core button */}
            <div
              className="w-18 h-18 rounded-full flex items-center justify-center relative"
              style={{
                width: 72, height: 72,
                background: isConverging
                  ? 'linear-gradient(135deg,#6D28D9,#4C1D95)'
                  : 'linear-gradient(135deg,#8B5CF6,#6D28D9)',
                boxShadow: isConverging
                  ? '0 0 80px rgba(109,40,217,1), 0 0 40px rgba(139,92,246,0.8)'
                  : '0 0 60px rgba(139,92,246,0.8)',
                transition: 'box-shadow 0.5s ease, background 0.5s ease',
              }}
            >
              {isConverging ? (
                <motion.div
                  animate={{ rotate: 360 }}
                  transition={{ duration: 1.5, repeat: Infinity, ease: 'linear' }}
                >
                  <Cpu className="w-8 h-8 text-white" />
                </motion.div>
              ) : (
                <svg className="w-8 h-8 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2}
                    d="M19 11a7 7 0 01-7 7m0 0a7 7 0 01-7-7m7 7v4m0 0H8m4 0h4m-4-8a3 3 0 01-3-3V5a3 3 0 116 0v6a3 3 0 01-3 3z" />
                </svg>
              )}
            </div>
          </motion.div>
        </AnimatePresence>

        {/* ── All chaos cards — scroll-driven torrent, drifts to center, no rotation ── */}
        <motion.div
          className="absolute inset-0 pointer-events-none z-20"
          style={{ opacity: groupOpacity, willChange: 'transform' }}
        >
          {allCards.map((c) => (
            <ChaosCard
              key={c.id}
              card={c}
              isTask={c.isTask}
              isMobile={isMobile}
              smoothProgress={smoothProgress}
              staggerOffset={c.staggerOffset}
            />
          ))}
        </motion.div>

        {/* ── Converging ── */}
        <AnimatePresence>
          {isConverging && (
            <motion.div key="conv"
              className="absolute inset-0 flex flex-col items-center justify-center z-30"
              initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} transition={{ duration: 0.6 }}>

              {/* Central glow pulse */}
              <motion.div className="absolute rounded-full"
                style={{ width: 320, height: 320, background: 'radial-gradient(circle,rgba(139,92,246,0.25),transparent 70%)', filter: 'blur(28px)' }}
                animate={{ scale: [0.7, 1.4, 0.7] }} transition={{ duration: 1.8, repeat: Infinity }} />

              {/* Incoming data streams — 8 lines radiating inward */}
              {[0, 45, 90, 135, 180, 225, 270, 315].map((deg, i) => (
                <motion.div key={deg} className="absolute"
                  style={{
                    width: 2, height: 80,
                    background: `linear-gradient(to bottom, transparent, ${i % 2 === 0 ? '#8B5CF6' : '#F59E0B'})`,
                    transformOrigin: 'bottom center',
                    transform: `rotate(${deg}deg) translateY(-120px)`,
                    borderRadius: 2,
                  }}
                  animate={{ opacity: [0, 0.8, 0], scaleY: [0.2, 1, 0.2] }}
                  transition={{ duration: 0.8, delay: i * 0.1, repeat: Infinity }}
                />
              ))}

              <div className="text-center z-10 mt-48">
                <motion.p className="text-xl text-[#EDEDED] font-medium mb-2"
                  animate={{ opacity: [0.5, 1, 0.5] }} transition={{ duration: 1.0, repeat: Infinity }}>
                  Neuron is sorting everything…
                </motion.p>
                <motion.p className="text-sm font-mono text-[#555] mb-4"
                  animate={{ opacity: [0.4, 0.8, 0.4] }} transition={{ duration: 1.4, repeat: Infinity, delay: 0.2 }}>
                  Tasks · Logs · Priorities · Dates · Context
                </motion.p>
                <div className="flex justify-center gap-2">
                  {[0, 1, 2, 3, 4].map(i => (
                    <motion.div key={i} className="w-1.5 h-1.5 rounded-full"
                      style={{ background: i % 2 === 0 ? '#8B5CF6' : '#F59E0B' }}
                      animate={{ y: [0, -12, 0], opacity: [0.3, 1, 0.3] }}
                      transition={{ duration: 0.55, delay: i * 0.1, repeat: Infinity }} />
                  ))}
                </div>
              </div>
            </motion.div>
          )}
        </AnimatePresence>

        {/* ── Organized state ── */}
        <AnimatePresence>
          {isOrganized && (
            <motion.div key="org"
              className="absolute inset-0 flex flex-col items-center justify-center z-30 px-4 w-full"
              initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} transition={{ duration: 0.5 }}>

              {/* Header badge */}
              <motion.div className="text-center mb-4 md:mb-6"
                initial={{ opacity: 0, y: -30 }} animate={{ opacity: 1, y: 0 }}
                transition={{ type: 'spring', stiffness: 90, damping: 14, delay: 0.05 }}>
                <div className="inline-flex items-center gap-2 px-3 py-1.5 md:px-4 md:py-2 rounded-full mb-2 md:mb-3"
                  style={{ background: 'rgba(16,185,129,0.1)', border: '1px solid rgba(16,185,129,0.3)' }}>
                  <motion.div className="w-1.5 h-1.5 md:w-2 md:h-2 rounded-full bg-[#10B981]"
                    animate={{ scale: [1, 1.6, 1] }} transition={{ duration: 1, repeat: Infinity }} />
                  {/* Live counter — counts to 21 then shows "thoughts sorted" */}
                  <span className="text-[10px] md:text-sm font-mono text-[#10B981]">
                    <LiveCounter /> thoughts sorted. Just like that.
                  </span>
                </div>
                <h2 className="text-2xl md:text-4xl font-medium text-[#EDEDED] tracking-tighter">
                  Everything, in its place.
                </h2>
              </motion.div>

              {/* Two panels — vertical stack on mobile, grid on desktop */}
              <div className="flex flex-col gap-2 md:gap-5 w-full max-w-4xl pb-4 md:grid md:grid-cols-2 md:pb-0">

                {/* Daily Logger */}
                <motion.div
                  initial={{ opacity: 0, y: 60, scale: 0.92 }}
                  animate={{ opacity: 1, y: 0, scale: 1 }}
                  transition={{ type: 'spring', stiffness: 85, damping: 13, delay: 0.15 }}
                  className="rounded-2xl overflow-hidden"
                  style={{ background: 'rgba(8,8,8,0.88)', border: '1px solid rgba(245,158,11,0.25)', backdropFilter: 'blur(16px)' }}>
                  <div className="px-3 py-2 md:px-4 md:py-2 flex items-center gap-2 md:gap-3"
                    style={{ background: 'rgba(245,158,11,0.07)', borderBottom: '1px solid rgba(245,158,11,0.15)' }}>
                    <motion.div className="w-1.5 h-1.5 md:w-2 md:h-2 rounded-full bg-[#F59E0B]"
                      animate={{ scale: [1, 1.5, 1] }} transition={{ duration: 1.5, repeat: Infinity }} />
                    <span className="text-[9px] md:text-sm font-mono uppercase tracking-wider text-[#F59E0B]">Daily Logger</span>
                    <span className="text-[8px] md:text-xs font-mono text-[#333] ml-auto">What happened</span>
                  </div>
                  <div className="p-2 md:p-4 space-y-2">
                    {(isMobile ? ORG_LOGS.slice(0, 2) : ORG_LOGS).map((log, i) => (
                      <motion.div key={i}
                        initial={{ opacity: 0, x: -20 }} animate={{ opacity: 1, x: 0 }}
                        transition={{ type: 'spring', stiffness: 100, damping: 15, delay: 0.25 + i * 0.08 }}
                        className="flex items-start gap-2 md:gap-3 rounded-lg md:rounded-xl px-3 py-2 md:px-4 md:py-2.5"
                        style={{ background: 'rgba(245,158,11,0.05)', border: '1px solid rgba(245,158,11,0.1)' }}>
                        <div className="w-1.5 h-1.5 rounded-full mt-1.5 flex-shrink-0 bg-[#F59E0B]" />
                        <div className="flex-1 min-w-0">
                          <p className="text-[#EDEDED] text-[11px] md:text-sm font-medium truncate">{log.text}</p>
                          <p className="text-[#444] text-[9px] md:text-xs mt-0.5">{log.time}</p>
                        </div>
                        <CheckCircle2 style={{ color: '#F59E0B', opacity: 0.45, width: 14, height: 14, flexShrink: 0, marginTop: 3 }} />
                      </motion.div>
                    ))}
                  </div>
                </motion.div>

                {/* Task AI */}
                <motion.div
                  initial={{ opacity: 0, y: 60, scale: 0.92 }}
                  animate={{ opacity: 1, y: 0, scale: 1 }}
                  transition={{ type: 'spring', stiffness: 85, damping: 13, delay: 0.22 }}
                  className="rounded-2xl overflow-hidden"
                  style={{ background: 'rgba(8,8,8,0.88)', border: '1px solid rgba(139,92,246,0.25)', backdropFilter: 'blur(16px)' }}>
                  <div className="px-3 py-2 md:px-4 md:py-2 flex items-center gap-2 md:gap-3"
                    style={{ background: 'rgba(139,92,246,0.07)', borderBottom: '1px solid rgba(139,92,246,0.15)' }}>
                    <motion.div className="w-1.5 h-1.5 md:w-2 md:h-2 rounded-full bg-[#8B5CF6]"
                      animate={{ scale: [1, 1.5, 1] }} transition={{ duration: 1.5, repeat: Infinity }} />
                    <span className="text-[9px] md:text-sm font-mono uppercase tracking-wider text-[#8B5CF6]">Task AI</span>
                    <span className="text-[8px] md:text-xs font-mono text-[#333] ml-auto">What's coming</span>
                  </div>
                  <div className="p-2 md:p-4 space-y-2">
                    {(isMobile ? ORG_TASKS.slice(0, 2) : ORG_TASKS).map((task, i) => (
                      <motion.div key={i}
                        initial={{ opacity: 0, x: 20 }} animate={{ opacity: 1, x: 0 }}
                        transition={{ type: 'spring', stiffness: 100, damping: 15, delay: 0.30 + i * 0.08 }}
                        className="flex items-start gap-2 md:gap-3 rounded-lg md:rounded-xl px-3 py-2 md:px-4 md:py-2.5"
                        style={{ background: `${task.color}0d`, border: `1px solid ${task.color}28` }}>
                        <div className="w-1.5 h-1.5 rounded-full mt-1.5 flex-shrink-0" style={{ background: task.color }} />
                        <div className="flex-1 min-w-0">
                          <span className="text-[8px] md:text-[9px] font-mono uppercase tracking-widest px-1.5 py-0.5 rounded inline-block mb-1"
                            style={{ color: task.color, background: `${task.color}22` }}>{task.label}</span>
                          <p className="text-[#EDEDED] text-[11px] md:text-sm font-medium truncate">{task.text}</p>
                          <p className="text-[#444] text-[9px] md:text-xs mt-0.5">{task.due}</p>
                        </div>
                      </motion.div>
                    ))}
                  </div>
                </motion.div>
              </div>

              <motion.p className="mt-6 text-center text-sm text-[#444] font-mono"
                initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 1.1 }}>
                Your brain is a supercomputer.{' '}
                <span style={{ color: '#8B5CF6', textShadow: '0 0 8px rgba(139,92,246,0.4)' }}>
                  Stop using it as a notepad.
                </span>
              </motion.p>
            </motion.div>
          )}
        </AnimatePresence>

        {/* ── Progress dots ── */}
        <motion.div
          className="absolute bottom-2 md:bottom-8 left-1/2 -translate-x-1/2 flex items-center gap-3 md:gap-5 z-40"
          style={{ opacity: uiOpacity }}
        >
          {[{ l: 'Chaos', a: isChaos }, { l: 'Process', a: isConverging }, { l: 'Clarity', a: isOrganized }].map((s, i) => (
            <React.Fragment key={s.l}>
              <div className="flex items-center gap-1.5">
                <motion.div className="w-1.5 h-1.5 rounded-full"
                  style={{ background: s.a ? '#8B5CF6' : '#252525' }}
                  animate={s.a ? { scale: [1, 1.8, 1] } : {}} transition={{ duration: 1.2, repeat: Infinity }} />
                <span className="text-[10px] font-mono uppercase tracking-widest"
                  style={{ color: s.a ? '#8B5CF6' : '#333' }}>{s.l}</span>
              </div>
              {i < 2 && <div className="w-8 h-px bg-[#1a1a1a]" />}
            </React.Fragment>
          ))}
        </motion.div>

      </div>
    </section>
  );
};

export default ChaosToClarity;
